import useMediaQuery from "@mui/material/useMediaQuery";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from "react-share";
import { sendTrackingEvent } from "../../../../helpers/tracking_management";
import useJobApplication from "../../../../hooks/useJobApplication";
import useTriggerSnackbar from "../../../../hooks/useTriggerSnackbar";
import {
  bookmarkJob,
  getJobV1,
  getStateRegions,
  unBookmarkJob,
  updateSelectedJob,
  updateSelectedJobId,
} from "../../../../redux/actions/job_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../../redux/actions/user_action";
import * as jobType from "../../../../redux/types/job_type";
import {
  ApplyContainer,
  ApplyJobButton,
  ArrowTopRightStyled,
  BookmarkFilledIconStyled,
  BookmarkIconStyled,
  ClipboardContainer,
  DivStyled,
  ExpiredCardStyled,
  HeroInfoIcon,
  IconButtonStyled,
  IconTextContainer,
  LabelStyled,
  LinkIconStyled,
  ScrapedJobText,
  ShareButtonsContainer,
  ShareIconStyled,
} from "./styles";

const SharedNationalityDialog = dynamic(() =>
  import("../../../shared/SharedNationalityDialog/SharedNationalityDialog")
);

const JobCountryPreferenceDialog = dynamic(() =>
  import("../../JobCountryPreferenceDialog/JobCountryPreferenceDialog")
);

function SingleJobApplyButtonGroup(props) {
  const { job, jobHighlight, stickyBottom } = props;
  const shareUrl = `${process.env.NEXT_PUBLIC_DOMAIN}/jobs/${job.slug}`;
  const title = `I found a job you might like!\n\n${job?.title} at ${job?.company?.name}\n\nHave a look here`;
  const belowLg = useMediaQuery("(max-width: 1279px)");
  const belowTablet = useMediaQuery("(max-width: 767px)");
  const aboveDesktop = useMediaQuery("(min-width: 1440px)");
  const [shareIconsOpen, setShareIconsOpen] = useState(false);
  const [applyLoadingStatus, setApplyLoadingStatus] = useState(true);
  const [bookmarkLoading, setBookmarkLoading] = useState(true);
  const [triggerSnackbarFunc] = useTriggerSnackbar();
  const reduxUser = useSelector((state) => state?.user?.user);
  const reduxJob = useSelector((state) => state?.jobs?.job);
  const jobCountryPreferenceDialogOpen = useSelector(
    (state) => state.user.showJobCountryPreferenceDialog
  );
  const reduxToggleStickyApply = useSelector(
    (state) => state?.navbar.showStickyApply
  );
  const dispatch = useDispatch();
  const router = useRouter();

  const isLoggedIn = checkAuthentication();

  const jobOrigin = "individual_job_page";
  const jobDiscovery = router.query.origin ?? "individual_job_page";

  const {
    applyJobFunc,
    checkLoggedIn,
    checkNationalityPhoneNumberLocation,
    checkRightToWorkAndRelocate,
    checkResumeRejected,
    checkScrapedJob,
    checkUtm,
  } = useJobApplication(jobOrigin, jobDiscovery);

  useEffect(() => {
    dispatch(getStateRegions());

    return () => {
      dispatch(updateSelectedJobId(""));
    };
  }, [dispatch]);

  useEffect(() => {
    if (jobCountryPreferenceDialogOpen) {
      setApplyLoadingStatus(false);
    }
  }, [jobCountryPreferenceDialogOpen]);

  async function handleApplyJob() {
    setApplyLoadingStatus(true);
    dispatch(updateSelectedJob(job));

    // NOT LOGGED IN AND HAS UTM CAMPAIGN
    if (checkUtm(job)) {
      sendTrackingEvent({
        event: "CE_click-apply-aggregator-presignup",
        "job-id": job?.id,
        "job-bucket-id": null,
        resume: false,
        logged_in: false,
        scraped: job?.category === "aggregated" ? true : false,
        "job-title": job?.title,
      });
      return;
    }

    const applySourceCE = "CE_job-application";

    // NOT LOGGED IN
    if (checkLoggedIn(job)) {
      sendTrackingEvent({
        event: applySourceCE,
        resume: false,
        logged_in: false,
        scraped: job?.category === "aggregated" ? true : false,
        "job-bucket-id": null,
        "job-title": job?.title,
        "job-id": job?.id,
      });
      return;
    }

    // CONDITION - NO NATIONALITY OR MOBILE NUMBER OR CURRENT LOCATION
    if (checkNationalityPhoneNumberLocation(job)) {
      return;
    }
    // CONDITION - RIGHT TO WORK OR RELOCATION PREFERENCE
    if (checkRightToWorkAndRelocate(job)) {
      return;
    }
    // CONDITION - NO RESUME / RESUME REJECTED
    // Do not send in application if user has no resume/rejected
    if (checkResumeRejected(job)) {
      sendTrackingEvent({
        event: applySourceCE,
        "job-title": job?.title ?? "",
        resume: false,
        logged_in: true,
        "job-bucket-id": null,
        scraped: job?.category === "aggregated" ? true : false,
        "job-id": job?.id,
      });
      return;
    }
    // CONDITION - SCRAPED JOB
    if (checkScrapedJob(job)) {
      if (jobDiscovery === "search") {
        sendTrackingEvent({
          event: applySourceCE,
          resume: true,
          logged_in: true,
          scraped: true,
          "job-bucket-id": null, // no bucket id due to not calling applyJob API
          "job-title": job?.title,
          "job-id": job?.id,
        });
      } else if (jobDiscovery === "rec_profile") {
        sendTrackingEvent({
          event: "CE_job-apply-click-rec_profile",
          "job-id": job?.id,
        });
      } else if (jobDiscovery === "rec_activity") {
        sendTrackingEvent({
          event: "CE_job-apply-click-rec_activity",
          "job-id": job?.id,
        });
      }
      return;
    }

    // CONDITION - DEFAULT JOB APPLICATION FLOW
    const params = {
      jobId: job.id,
      canCancel: false,
      origin: jobOrigin,
      jobDiscovery: jobDiscovery,
      applySource: "jdp_apply_now",
    };

    const response = await applyJobFunc(job, params);
    if (response.success) {
      sendTrackingEvent({
        event: applySourceCE,
        resume: true,
        logged_in: true,
        scraped: false,
        "job-bucket-id": response?.payload?.jobBucketId,
        "job-title": response?.payload?.appliedJob?.title,
        "job-id": job?.id,
      });
      setApplyLoadingStatus(false);
    }
  }

  async function handleBookmarkClicked(targetJobId) {
    setBookmarkLoading(true);
    if (typeof reduxUser === "object" && Object.keys(reduxUser).length !== 0) {
      if (reduxJob.bookmark) {
        let params = {
          jobId: targetJobId,
          canCancel: false,
          origin: "individual-job",
          appliedDialog: false,
        };
        const unbookmarkRes = await dispatch(unBookmarkJob(params));

        if (unbookmarkRes.type === jobType.UNBOOKMARK_JOB_SUCCEED) {
          triggerSnackbarFunc({
            snackbarMessage: "Bookmark removed successfully.",
            severity: "success",
          });
        } else {
          triggerSnackbarFunc({
            snackbarMessage: "Could not remove bookmark, please try again",
            severity: "error",
          });
        }
      } else {
        let params = {
          jobId: targetJobId,
          canCancel: false,
          origin: "individual-job",
          appliedDialog: false,
        };
        const bookmarkRes = await dispatch(bookmarkJob(params));

        if (bookmarkRes.type === jobType.BOOKMARK_JOB_SUCCEED) {
          triggerSnackbarFunc({
            snackbarMessage: "Bookmark job successfully.",
            severity: "success",
          });
        } else {
          triggerSnackbarFunc({
            snackbarMessage: "Could not bookmark job, please try again",
            severity: "error",
          });
        }
      }
    } else {
      dispatch(updateSelectedJob(job)).then(() => {
        dispatch(updateSignUpOrigin("save_job")).then(() => {
          dispatch(updateShowSignInModalStatus(true));
        });
      });
    }

    setBookmarkLoading(false);
  }

  const onShareIconClicked = () => {
    setShareIconsOpen(!shareIconsOpen);
  };

  async function handlePopulateJobInRedux() {
    if (reduxUser) {
      if (!bookmarkLoading || !applyLoadingStatus) {
        setApplyLoadingStatus(true);
        setBookmarkLoading(true);
      }

      dispatch(
        getJobV1({
          jobId: job.id ?? "",
        })
      ).then(() => {
        setApplyLoadingStatus(false);
        setBookmarkLoading(false);
      });
    } else {
      setApplyLoadingStatus(false);
      setBookmarkLoading(false);
    }
  }

  async function copyToClipboard() {
    if (!navigator.clipboard) {
      // Clipboard API not available
      return;
    }
    const text = `${title}: ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(text);
      triggerSnackbarFunc({
        snackbarMessage: "Link copied successfully.",
        severity: "success",
      });
    } catch (error) {
      triggerSnackbarFunc({
        snackbarMessage: "Link failed to copy.",
        severity: "error",
      });
    }
  }

  useEffect(() => {
    handlePopulateJobInRedux();
  }, [job.id]);

  return (
    <ApplyContainer
      $stickybottom={stickyBottom?.toString() ?? "false"}
      // only sticky bottom visibility should be affected by the redux value.
      // otherwise, other reference to this component should always be visible
      visibility={
        stickyBottom
          ? reduxToggleStickyApply && belowLg
            ? "visible"
            : "hidden"
          : "visible"
      }
    >
      {job.active ? (
        <DivStyled
          position={"relative"}
          container
          justifyContent={
            stickyBottom
              ? "center"
              : belowTablet
              ? "space-between"
              : belowLg
              ? "flex-start"
              : jobHighlight
              ? "space-between"
              : "flex-end"
          }
          flexWrap={"nowrap"}
          alignItems={"flex-start"}
          columnGap={"16px"}
          rowGap={1}
        >
          <DivStyled
            width={
              jobHighlight
                ? aboveDesktop
                  ? "83%"
                  : "80%"
                : belowTablet
                ? "60%"
                : null
            }
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <ApplyJobButton
              variant="contained"
              aria-label="apply-job-button"
              onClick={handleApplyJob}
              loading={applyLoadingStatus}
              disabled={reduxJob.haveApplied}
              sx={{
                width: jobHighlight ? "100%" : null,
              }}
            >
              {reduxJob.haveApplied
                ? "Applied"
                : job?.category === "organic"
                ? "Quick Apply"
                : "Apply Now"}
              {(job?.category === "scraped" ||
                job?.category === "aggregated") &&
              !reduxJob.haveApplied ? (
                isLoggedIn ? (
                  <ArrowTopRightStyled />
                ) : null
              ) : null}
            </ApplyJobButton>

            {job?.category === "aggregated" ? (
              <ScrapedJobText variant="subtitle1" textTransform={"uppercase"}>
                from partner platform
              </ScrapedJobText>
            ) : null}
          </DivStyled>

          {job.category !== "aggregated" ? (
            <IconButtonStyled
              aria-label="bookmark-job-button"
              onClick={() => handleBookmarkClicked(job.id)}
              disabled={bookmarkLoading === true}
            >
              {reduxJob.bookmark ? (
                <BookmarkFilledIconStyled />
              ) : (
                <BookmarkIconStyled />
              )}
            </IconButtonStyled>
          ) : null}

          {!jobHighlight || job?.category === "aggregated" ? (
            <IconButtonStyled
              aria-label="share-job-button"
              onClick={onShareIconClicked}
            >
              <ShareIconStyled />
              {shareIconsOpen && (
                <ShareButtonsContainer>
                  <FacebookShareButton
                    url={shareUrl}
                    quote={title}
                    id={"facebook-job-link-sharing-button"}
                    style={{
                      height: "32px",
                    }}
                    onClick={() => {
                      sendTrackingEvent({
                        event: "CE_click-share-ijp",
                        item: "facebook",
                        "job-id": job?.id,
                      });
                    }}
                  >
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={shareUrl}
                    title={title}
                    id={"twitter-job-link-sharing-button"}
                    style={{
                      height: "32px",
                    }}
                    onClick={() => {
                      sendTrackingEvent({
                        event: "CE_click-share-ijp",
                        item: "twitter",
                        "job-id": job?.id,
                      });
                    }}
                  >
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <LinkedinShareButton
                    url={shareUrl}
                    summary={title}
                    id={"linkedin-job-link-sharing-button"}
                    style={{
                      height: "32px",
                    }}
                    onClick={() => {
                      sendTrackingEvent({
                        event: "CE_click-share-ijp",
                        item: "linkedin",
                        "job-id": job?.id,
                      });
                    }}
                  >
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                  <WhatsappShareButton
                    url={shareUrl}
                    title={title}
                    separator=": "
                    id={"whatsapp-job-link-sharing-button"}
                    style={{
                      height: "32px",
                    }}
                    onClick={() => {
                      sendTrackingEvent({
                        event: "CE_click-share-ijp",
                        item: "whatsapp",
                        "job-id": job?.id,
                      });
                    }}
                  >
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <EmailShareButton
                    url={shareUrl}
                    subject={title}
                    id={"email-job-link-sharing-button"}
                    style={{
                      height: "32px",
                    }}
                    onClick={() => {
                      sendTrackingEvent({
                        event: "CE_click-share-ijp",
                        item: "email",
                        "job-id": job?.id,
                      });
                    }}
                  >
                    <EmailIcon size={32} round />
                  </EmailShareButton>
                  <ClipboardContainer id={"copy-job-link-sharing-button"}>
                    <LinkIconStyled onClick={copyToClipboard} />
                  </ClipboardContainer>
                </ShareButtonsContainer>
              )}
            </IconButtonStyled>
          ) : null}
          {/* NOT RELATED TO COMPONENT (e.g. dialogs) */}
          <SharedNationalityDialog
            job={props.job}
            onApplyJobClicked={handleApplyJob}
            useLocalApply={true}
            jobOrigin={"individual-job-page"}
          />
          <JobCountryPreferenceDialog
            onApplyJobClicked={handleApplyJob}
            job={props.job}
            useLocalApply={true}
            origin={"individual-job-page"}
          />
        </DivStyled>
      ) : (
        <ExpiredCardStyled>
          <IconTextContainer variant="h6">
            <HeroInfoIcon />
            <LabelStyled>This job is no longer available</LabelStyled>
          </IconTextContainer>
        </ExpiredCardStyled>
      )}
    </ApplyContainer>
  );
}
export default SingleJobApplyButtonGroup;
